import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    // Reconcile state when the dialog is closed by anything other than closeDialog()
    // — e.g. a third-party script (Razorpay Magic Checkout) calling the native
    // dialog.close() directly on checkout, which bypasses our close path.
    this.refs.dialog.addEventListener('close', this.#handleNativeClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.refs.dialog.removeEventListener('close', this.#handleNativeClose);
  }

  /**
   * Runs on every native `close` (including closes triggered outside closeDialog).
   * Removes the interaction listeners showDialog() added, so an external close
   * doesn't leave them attached to accumulate across repeated open→close cycles.
   * closeDialog() also removes them up front; removeEventListener is idempotent, so
   * the overlap is harmless. Intentionally does NOT dispatch DialogCloseEvent: on an
   * external close we don't want to trigger DialogHistory's `history.back()`, which
   * would race a third-party redirect. The scroll-lock releases on its own (it's CSS
   * derived from `[open]`), and the history entry is moot (desktop never pushes it;
   * mobile unloads on redirect).
   */
  #handleNativeClose = () => {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
  };

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   *
   * Background scroll-lock is handled declaratively in CSS off `dialog:modal`
   * (see assets/base.css) — no body-style mutation here, so it can't leak if the
   * dialog is dismissed off the normal close path.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Defer to the next frame so the click that opened the dialog finishes
    // propagating before the outside-click listener attaches. The trigger sits
    // outside the dialog, so attaching synchronously would let that same click
    // bubble to #handleClick and immediately close the just-opened dialog.
    requestAnimationFrame(() => {
      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

/**
 * Binds a dialog's open/close lifecycle to a browser-history entry so the Back button — including
 * Android's system back — closes the dialog instead of navigating away from the page.
 *
 * Single source of truth for that behaviour: both the cart drawer and the PDP zoom dialog use it
 * (they previously hand-rolled near-identical copies). The owner constructs one instance and wires
 * it: `reset()` on connect (clears a stale entry from a prior page state), `push()` when the dialog
 * opens, `pop()` on any close, and `destroy()` on disconnect. When Back pops our entry, `onBack`
 * fires — close the dialog there. Calling `pop()` from inside that close is safe (it no-ops once the
 * entry is gone), so callers don't need to special-case the back-initiated path.
 */
export class DialogHistory {
  /** @type {string} */
  #key;
  /** @type {() => void} */
  #onBack;
  /** @type {AbortController | null} */
  #controller = null;
  #pushed = false;

  /**
   * @param {object} options
   * @param {string} options.key - Unique `history.state` key identifying this dialog's entry.
   * @param {() => void} options.onBack - Called when Back pops our entry; close the dialog here.
   */
  constructor({ key, onBack }) {
    this.#key = key;
    this.#onBack = onBack;
  }

  /** Clears a stale entry for this dialog left in `history.state` by a prior navigation. */
  reset() {
    if (history.state?.[this.#key]) history.replaceState(null, '');
  }

  /** Pushes a history entry when the dialog opens. No-op if already pushed. */
  push() {
    if (this.#pushed) return;

    this.#pushed = true;

    if (!history.state?.[this.#key]) history.pushState({ [this.#key]: true }, '');

    this.#controller = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#controller.signal });
  }

  /**
   * Pops our entry on a user-initiated close so a later Back doesn't land on a dead entry. No-op if
   * the entry was already consumed by Back (the popstate handler tears down before firing `onBack`).
   */
  pop() {
    if (!this.#pushed) return;
    this.#teardown();

    // Consume our own entry so a later Back doesn't land on a dead one.
    if (history.state?.[this.#key]) history.back();
  }

  /** Releases the popstate listener without navigating. */
  destroy() {
    this.#teardown();
  }

  #teardown() {
    this.#pushed = false;
    this.#controller?.abort();
    this.#controller = null;
  }

  #handlePopState = () => {
    // Back consumed our entry. Tear down first so the close's own pop() no-ops, then close.
    this.#teardown();
    this.#onBack();
  };
}

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}
